import Avatar from '@orn/shared-web/components/Avatar';
import Dropdown from '@orn/shared-web/components/Dropdown/Dropdown';
import { LogOutIcon, MenuIcon, SettingsIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import { cx, sortCx } from '@orn/shared-web/utils/cx';
import formatUserDisplayName from '@orn/shared-web/utils/formatUserDisplayName';
import { getInitial } from '@orn/shared-web/utils/getInitials';
import * as React from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useLogout } from '../../hooks/mutations/useLogout';
import { PATHS } from '../../paths';

import DebugAccountSubmenu from './DebugAccountSubmenu';
import { ICON_RAIL_WIDTH_PX } from './dimensions';
import MantleAssistantNavButton from './MantleAssistantNavButton';
import NotificationBellButton from './NotificationBellButton';

import type { NavItemType } from './config';
import type { LoggedInUser } from '../../providers/AuthProvider';

const itemStyles = sortCx({
  root: 'group flex w-full flex-col items-center justify-center gap-1.5 rounded-md p-1 outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2',
  rootSelected: 'bg-active hover:bg-secondary_hover',
});

const getPathname = (href?: string) => href?.split('?')[0] ?? '';

interface IIconRailNavItemProps {
  item: NavItemType;
  current: boolean;
}

const IconRailNavItem = ({ item, current }: IIconRailNavItemProps) => {
  // Rail-specific icon override (e.g. an icon-anchored badge variant)
  // wins over the default. Falls back to `icon` for items that look
  // identical in both surfaces.
  const Icon = item.iconRailIcon ?? item.icon;
  if (!Icon || !item.href) {
    return null;
  }

  return (
    <RouterLink
      to={item.href}
      aria-current={current ? 'page' : undefined}
      className={cx(itemStyles.root, current && itemStyles.rootSelected)}
    >
      <Icon
        aria-hidden="true"
        className={cx('text-fg-quaternary size-[22px] shrink-0', current && 'text-fg-brand-primary')}
      />
      {/*
       * `whitespace-nowrap` keeps each label on one line. We intentionally do
       * not clip or ellipsise overflow — Figma's spec implies a 48px content
       * box (4px ul padding + 4px link padding × 2) but its preview renders
       * labels visibly past the box edge. Real browsers behave the same way
       * with `overflow: visible`, so labels like "Recurring" extend a few
       * pixels into the link's padding zone but still sit inside the 64px
       * rail width.
       *
       * Items whose default label is too long for that pattern (e.g. "Bills
       * & Subscriptions") provide an `iconRailLabel` override so the rail
       * uses a shorter form while the full sidebar / drawer keep the long
       * label. Falls back to `item.label` when omitted.
       */}
      <span
        className={cx('text-text-secondary whitespace-nowrap text-xs font-semibold', current && 'text-text-primary')}
      >
        {item.iconRailLabel ?? item.label}
      </span>
    </RouterLink>
  );
};

interface IIconRailAccountButtonProps {
  user: LoggedInUser;
}

const IconRailAccountButton = ({ user }: IIconRailAccountButtonProps) => {
  const navigate = useNavigate();
  const [logout] = useLogout();

  const handleLogout = React.useCallback(async () => {
    try {
      await logout();
      window.location.href = '/';
    } catch (error) {
      logger.error('Failed to logout:', error);
    }
  }, [logout]);

  const displayName = formatUserDisplayName(user) ?? user.name;

  return (
    <Dropdown.Root>
      <AriaButton
        aria-label={`Account menu for ${displayName}`}
        className={({ isPressed, isFocusVisible }) =>
          cx(
            'outline-focus-ring cursor-pointer rounded-full transition-colors hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2',
            (isPressed || isFocusVisible) && 'opacity-80'
          )
        }
      >
        <Avatar
          size="md"
          src={user.avatarUrl}
          contrastBorder={!user.avatarUrl}
          alt={displayName}
          initials={getInitial(displayName)}
        />
      </AriaButton>

      <Dropdown.Popover placement="right">
        <Dropdown.Menu>
          <Dropdown.Item icon={SettingsIcon} onAction={() => navigate(PATHS.SETTINGS)}>
            Settings
          </Dropdown.Item>
          <DebugAccountSubmenu />
          <Dropdown.Item icon={LogOutIcon} onAction={handleLogout}>
            Log out
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
};

interface IIconRailSidebarProps {
  user: LoggedInUser;
  /** URL of the currently active item. */
  activeUrl?: string;
  /** Primary nav items. Items with sub-items (e.g. Admin) are filtered out — sub-menus don't fit at 64px. */
  items: NavItemType[];
  /** Invoked when the user taps the hamburger button at the top of the rail. */
  onOpenDrawer: () => void;
  /**
   * When `true`, render the compact "Ask Mantle" button in the footer
   * above the account avatar. Caller is responsible for resolving the
   * `feat-assistant` gate.
   */
  showAssistantButton?: boolean;
  /**
   * When `true`, render the alerts bell below the hamburger. Caller is
   * responsible for resolving the `feat-alerts-center` gate.
   */
  showAlertsBell?: boolean;
  /** Unread alert count shown on the bell's pip. */
  alertsUnreadCount?: number;
  /** Whether the alerts route is the active one. */
  isAlertsCurrent?: boolean;
  /** Opens the center in the `xl+` side panel instead of navigating. */
  onOpenAlertsInPanel?: () => void;
  /** Additional CSS classes. */
  className?: string;
}

/**
 * 64px icon-with-label sidebar shown at the `md` breakpoint. Each nav item
 * renders its 22px icon above a 12px text-xs label (semibold,
 * `text-secondary`), per Figma node `3502:3245`. Collapsible sub-menus
 * (e.g. Admin) are filtered out because they cannot fit at this width.
 *
 * The top of the rail is a hamburger that opens the same drawer the mobile
 * top bar uses (`MobileNavDrawer`), giving md-viewport users access to the
 * full nav surface. The Mantle brand mark is intentionally absent at this
 * width — the wordmark appears in the drawer when opened.
 */
const IconRailSidebar = ({
  user,
  activeUrl,
  items,
  onOpenDrawer,
  showAssistantButton = false,
  showAlertsBell = false,
  alertsUnreadCount = 0,
  isAlertsCurrent = false,
  onOpenAlertsInPanel,
  className,
}: IIconRailSidebarProps) => {
  const primaryItems = items.filter((item) => item.href && item.icon && !item.items?.length && !item.divider);

  return (
    <aside
      style={{ width: ICON_RAIL_WIDTH_PX }}
      className={cx('flex h-full flex-col items-center justify-between py-3', className)}
    >
      <div className="flex w-full flex-col items-center gap-4">
        <AriaButton
          aria-label="Open navigation menu"
          onPress={onOpenDrawer}
          className={({ isPressed, isFocusVisible }) =>
            cx(
              'outline-focus-ring hover:bg-primary_hover flex cursor-pointer items-center justify-center rounded-md p-2 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2',
              (isPressed || isFocusVisible) && 'bg-primary_hover'
            )
          }
        >
          <MenuIcon aria-hidden="true" className="text-fg-quaternary size-6 shrink-0" />
        </AriaButton>
        {showAlertsBell && (
          <NotificationBellButton
            unreadCount={alertsUnreadCount}
            current={isAlertsCurrent}
            onOpenInPanel={onOpenAlertsInPanel}
          />
        )}
        <ul aria-label="Main navigation" className="flex w-full flex-col items-center gap-3 px-1">
          {primaryItems.map((item) => (
            <li key={item.label} className="w-full">
              <IconRailNavItem item={item} current={getPathname(item.href) === activeUrl} />
            </li>
          ))}
        </ul>
      </div>
      <div className="flex flex-col items-center gap-3">
        {showAssistantButton && <MantleAssistantNavButton variant="compact" />}
        <IconRailAccountButton user={user} />
      </div>
    </aside>
  );
};

export default IconRailSidebar;
